// Manual access: a paid plan with no live Stripe subscription behind it, so nothing
// bills for it. The backend rule is isManualAccess in back-end-next/src/lib/lifecycle.ts;
// this mirrors it for the edit form, which has to know before saving.

/** Statuses of a subscription that is still running, as in the backend. */
export const LIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due', 'unpaid'];

/**
 * Whether saving these values leaves the agency on manual access. `stripeStatus` is
 * the edit form's value, where 'free' means no status; `planName` is the plan's
 * internal name (undefined while none is picked).
 */
export function willBeManualAccess(s: {
  planName: string | undefined;
  subscriptionId: string;
  stripeStatus: string;
}): boolean {
  if (!s.planName || s.planName === 'free') return false;
  return !(s.subscriptionId.trim() !== '' && LIVE_SUBSCRIPTION_STATUSES.includes(s.stripeStatus));
}

/** The first line of the Manual badge's tooltip. */
export function manualAccessTooltip(reason: string | null | undefined): string {
  return `Manual access: ${reason?.trim() || 'no reason given'}`;
}

/**
 * Whether the edit form shows the manual access notice and asks for a reason. The
 * save judges a subscription by its live Stripe status, which the form knows only for
 * the stored subscription or after Sync, so a newly typed id is left to the save.
 */
export function modalManualAccess(s: {
  planName: string | undefined;
  subscriptionId: string;
  storedSubscriptionId: string;
  /** Sync pulled this subscription's status from Stripe. */
  synced: boolean;
  stripeStatus: string;
}): boolean {
  const id = s.subscriptionId.trim();
  const statusKnown = id === '' || id === s.storedSubscriptionId || s.synced;
  return statusKnown && willBeManualAccess(s);
}

/**
 * What the form's read-only Stripe Status field shows, and the line under it. With no
 * subscription the save keeps no live status, but it keeps one set by hand (canceled).
 * A trialing one stays visible: the form's trialing error explains it.
 */
export function stripeStatusField(s: { subscriptionId: string; stripeStatus: string }): {
  shown: string;
  helper: string;
} {
  if (s.subscriptionId.trim() !== '') {
    return { shown: s.stripeStatus, helper: 'Pulled from the subscription above' };
  }
  if (s.stripeStatus === 'trialing') {
    return { shown: s.stripeStatus, helper: 'No subscription, so no status is saved' };
  }
  if (s.stripeStatus === 'free' || LIVE_SUBSCRIPTION_STATUSES.includes(s.stripeStatus)) {
    return { shown: 'free', helper: 'No subscription, so no status is saved' };
  }
  return { shown: s.stripeStatus, helper: 'Set by hand, there is no subscription to pull it from' };
}

/** The save's answer when it leaves manual access but got no reason (PATCH /admin/agencies/[id]). */
export function isManualAccessRefusal(message: string | undefined): boolean {
  return !!message && message.startsWith('Add a reason for manual access');
}
